import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import MailAPI from '@/services/api-calls/mail.api';

interface UnreadCountsState {
  // Folder-level counts (inbox/sent/drafts/trash/spam/archived/custom folders)
  folders: Record<string, number>;
  // Category/label-level counts (primary/social/promotions/updates/forums + custom labels)
  categories: Record<string, number>;
  status: 'idle' | 'loading' | 'error';
}

const initialState: UnreadCountsState = {
  folders: {},
  categories: {},
  status: 'idle',
};

// NOTE on response shape: this codebase's `ServerResponse.success(res, data, message)`
// (backend/utils/server-response.ts) sends `{ success: true, message, ...data }` —
// it spreads the payload at the TOP LEVEL of the JSON body, it does not nest it
// under a `data` key. The axios instance in `services/api.ts` does no further
// unwrapping (its response interceptor only handles 401 retry and otherwise
// returns the response untouched), so `MailAPI.fetchFolders()` /
// `MailAPI.fetchUnReadMailCounts()` (which each already return `response.data`)
// resolve directly to:
//   fetchFolders()            -> { success, message, folders: [{name, total, unread, type}] }
//   fetchUnReadMailCounts()   -> { success, message, categories, totalUnread, categoryWiseUnread: { standard, custom } }
// i.e. flat, not `response.data.folders` / `response.data.categoryWiseUnread`.
export const fetchUnreadCounts = createAsyncThunk(
  'unreadCounts/fetch',
  async () => {
    const [foldersResponse, categoriesResponse] = await Promise.all([
      MailAPI.fetchFolders(),
      MailAPI.fetchUnReadMailCounts(),
    ]);

    const folderList = foldersResponse?.folders || [];
    const folders: Record<string, number> = {};
    folderList.forEach((f: { name: string; unread: number }) => {
      folders[f.name] = f.unread;
    });

    const categoryWiseUnread = categoriesResponse?.categoryWiseUnread || { standard: {}, custom: {} };
    const categories: Record<string, number> = {
      ...categoryWiseUnread.standard,
      ...categoryWiseUnread.custom,
    };

    return { folders, categories };
  }
);

const unreadCountsSlice = createSlice({
  name: 'unreadCounts',
  initialState,
  reducers: {
    decrementUnread: (
      state,
      action: PayloadAction<{ scope: 'folders' | 'categories'; key: string; by?: number }>
    ) => {
      const { scope, key, by = 1 } = action.payload;
      const current = state[scope][key] ?? 0;
      state[scope][key] = Math.max(0, current - by);
    },
    incrementUnread: (
      state,
      action: PayloadAction<{ scope: 'folders' | 'categories'; key: string; by?: number }>
    ) => {
      const { scope, key, by = 1 } = action.payload;
      state[scope][key] = (state[scope][key] ?? 0) + by;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadCounts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUnreadCounts.fulfilled, (state, action) => {
        state.folders = action.payload.folders;
        state.categories = action.payload.categories;
        state.status = 'idle';
      })
      .addCase(fetchUnreadCounts.rejected, (state) => {
        state.status = 'error';
      });
  },
});

export const { decrementUnread, incrementUnread } = unreadCountsSlice.actions;
export default unreadCountsSlice.reducer;
